import { useSessionStorage } from '@/hooks'
import { createContext, useContext, useEffect, useMemo } from 'react'
import type { UserLinkedOrganisation } from './useUserLinkedProjects'

type ProjectsOrganisationFilterContextValue = {
  organisationFilter: string | null
  setOrganisationFilter: (value: string | null) => void
  isMounted: boolean
}

const ProjectsOrganisationFilterContext = createContext<ProjectsOrganisationFilterContextValue | undefined>(undefined)

export function ProjectsOrganisationFilterProvider({ children }: { children: React.ReactNode }) {
  const [organisationFilter, setOrganisationFilter, , isMounted] = useSessionStorage<string | null>(
    'projectsHomeOrganisationFilter',
    null
  )
  const value = useMemo(
    () => ({ organisationFilter, setOrganisationFilter, isMounted }),
    [organisationFilter, setOrganisationFilter, isMounted]
  )

  return (
    <ProjectsOrganisationFilterContext.Provider value={value}>{children}</ProjectsOrganisationFilterContext.Provider>
  )
}

export function useProjectsOrganisationFilter(organisations: UserLinkedOrganisation[]) {
  const context = useContext(ProjectsOrganisationFilterContext)
  if (!context) {
    throw new Error('useProjectsOrganisationFilter must be used within ProjectsOrganisationFilterProvider')
  }

  const { organisationFilter, setOrganisationFilter, isMounted } = context
  const organisationOptions = useMemo(
    () =>
      organisations
        .map((organisation) => ({ value: organisation.name, label: organisation.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [organisations]
  )
  const selectedOrganisation = useMemo(() => {
    if (organisationOptions.length === 0) {
      return null
    }

    const isCurrentSelectionValid =
      organisationFilter != null && organisationOptions.some((option) => option.value === organisationFilter)

    if (isCurrentSelectionValid) {
      return organisationFilter
    }

    const defaultOrganisation = organisations.find((organisation) => organisation.isDefault)
    return defaultOrganisation?.name ?? organisationOptions[0]?.value ?? null
  }, [organisationFilter, organisationOptions, organisations])

  useEffect(() => {
    if (isMounted && selectedOrganisation != null && selectedOrganisation !== organisationFilter) {
      setOrganisationFilter(selectedOrganisation)
    }
  }, [isMounted, organisationFilter, selectedOrganisation, setOrganisationFilter])

  return { organisationFilter, organisationOptions, selectedOrganisation, setOrganisationFilter }
}
